// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages } from "react-intl";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
  ActiveElement,
  ChartEvent,
  LegendItem,
  LegendElement,
  ChartType,
} from "chart.js";

import { Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import TariffCircuitListModel from "model/tariffcircuitlist";
import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { IState } from "states/states";
import { isSet } from "shared/utils/utils";
import ChartBaseComponent, {
  IChartBaseDispatch,
  IChartBaseExtProps,
  IChartBaseProps,
  mapStateToProps as baseMapStateToProps,
  mapDispatchToProps as baseMapDispatchToProps,
  IChartTariffNode,
  IChartBaseState,
} from "./ChartBase";
import cssContent from "constants.module.scss";

interface IPowerPieChartExtProps extends IChartBaseExtProps {}

interface IPowerPieChartProps extends IChartBaseProps {}

interface IPowerPieChartState extends IChartBaseState {
  hiddenData: number[];
}

interface IPowerPieChartDispatch extends IChartBaseDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const mapStateToProps = (state: IState, ownProps: IPowerPieChartExtProps) => {
  const props: IPowerPieChartProps = {
    ...baseMapStateToProps(state, ownProps),
  };
  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IPowerPieChartDispatch = {
    ...baseMapDispatchToProps(dispatch),
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  internalError: {
    id: "internal error...",
  },
  noDataAvailable: {
    id: "No data available.",
  },
});

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

class PowerPieChartComponent extends ChartBaseComponent<
  IPowerPieChartProps & IPowerPieChartExtProps & IPowerPieChartDispatch,
  IPowerPieChartState
> {
  state = { hiddenData: [] } as IPowerPieChartState;
  private tlEntries: IChartTariffNode[] = [];

  // render pie-view
  private renderPieView() {
    const values: number[] = [];
    const labels: string[] = [];
    const backgroundColors: string[] = [];
    const borderColors: string[] = [];
    const hierarchyValues: number[] = [];
    const hierarchyLabels: string[] = [];
    const hierarchyBackgroundColors: string[] = [];
    const hierarchyBorderColors: string[] = [];

    const allItems = this.getSelectedTariffCircuits(
      this.props.isHierarchyViewMode === false, // show all tariffs when not in hierarchy mode
      false, // show only selected categories in color
      false, // only color selected tariffs, not the whole hierarchy
    );

    const selectedItems = allItems.tlOptions;

    this.tlEntries = allItems.selectedTariffs;

    this.tlEntries.forEach((tariff, curIndex) => {
      const isHighlighted =
        !this.state.hiddenData.includes(curIndex - 1) &&
        (!isSet(this.props.filterText) ||
          this.props.filterText === "" ||
          tariff.name.toLocaleLowerCase().includes(this.props.filterText.toLocaleLowerCase()));

      const value = Math.abs(tariff.value ?? 0);
      const label = `${selectedItems[curIndex].text}`;
      const backgroundColor = `${
        tariff?.isVisible === true && tariff?.isDisabled === false && isHighlighted ?
          tariff?.color
        : cssContent.GRAPH_GRAYED_OUT
      }40`;
      const borderColor = `${isHighlighted ? tariff?.color : cssContent.GRAPH_GRAYED_OUT}`;

      // first entry is Root-Hierarchy!
      if (curIndex === 0) {
        hierarchyValues.push(value);
        hierarchyLabels.push(label);
        hierarchyBackgroundColors.push(backgroundColor);
        hierarchyBorderColors.push(borderColor);
      } else {
        values.push(value);
        labels.push(label);
        backgroundColors.push(backgroundColor);
        borderColors.push(borderColor);
      }
    });

    // only add labels from children to the dataset-legend...
    // and hierarchy-pie-piece to the top of the drawing - which means
    // use as second dataset (last entry)
    const data = {
      labels: [...labels],
      datasets: [
        {
          label: "TariffCircuit values",
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
        },
        {
          label: "TariffCircuit selected hierarchy",
          data: hierarchyValues,
          backgroundColor: hierarchyBackgroundColors,
          borderColor: hierarchyBorderColors,
          borderWidth: 0,
        },
      ],
    };

    const options: ChartOptions<"pie"> = {
      animation: false,
      plugins: {
        legend: {
          display: true,
          position: "right" as const,
          //maxWidth: 1000,
          labels: {
            generateLabels: (chart) => {
              return this.tlEntries
                .filter((tl, idx) => idx !== 0)
                .map((tl, idx) => {
                  return {
                    text: tl.name,
                    fillStyle: backgroundColors[idx],
                    strokeStyle: borderColors[idx],
                    hidden: this.state.hiddenData.includes(idx),
                    datasetIndex: 0,
                    index: idx,
                  } as LegendItem;
                }) as LegendItem[];
            },
          },
          onClick: (evt, legendItem, legend) =>
            this.legendItemClick(evt, legendItem, legend as LegendElement<ChartType>),
        },
        tooltip: {
          enabled: true,
          callbacks: {
            title: () => {
              return "";
            },
            label: (tooltipItem) => {
              // the index-order is different to our tlEntries and tlOptions,
              // since we place the root-hierarch on top / at last to the datasets
              // so check the dataIndex / datasetIndex ->
              // use the first entry if dataset 1 is queried
              // use the dataIndex + 1 to jump over the root-entry

              const tariffCircuit =
                tooltipItem.datasetIndex === 0 ?
                  this.tlEntries[tooltipItem.dataIndex + 1]
                : this.tlEntries[0];

              // do we face a background fill data set?
              return `${tariffCircuit.name}: ${tariffCircuit.valueText}`;
            },
          },
        },
        datalabels: {
          display: true,
          textAlign: "center",

          formatter: (value, context) => {
            // hide labels for zero values
            if (value === 0) return "";

            // since we place the root-hierarch on top / at last to the datasets
            // so check the dataIndex / datasetIndex ->
            // use the first entry if dataset 1 is queried
            // use the dataIndex + 1 to jump over the root-entry

            const tariffCircuit =
              context.datasetIndex === 0 ?
                this.tlEntries[context.dataIndex + 1]
              : this.tlEntries[0];

            return [
              `${context.datasetIndex > 0 ? tariffCircuit?.name : ""}`,
              `${tariffCircuit?.valueText}`,
            ];
          },
        },
      },
      onClick: (event, elements, chart) => this.onChartClick(event, elements),
      onHover: (event, elements, chart) => this.checkMouse(elements),
    };
    // add style definitions of custom emptyPie plugin
    (options.plugins as any).emptyPie = {
      color: "rgba(0, 0, 0, 0.5)",
      width: 2,
      radiusDecrease: 20,
    };

    return (
      <div style={{ width: "600px", margin: "auto" }}>
        <Pie
          data={data}
          options={options}
          plugins={[
            {
              id: "emptyPie",
              afterDatasetDraw: (chart, args, options) => {
                const { datasets } = chart.data;
                const { color, width, radiusDecrease } = options;
                let hasData = false;
                let allHidden = true;
                const {
                  chartArea: { left, top, right, bottom },
                  ctx,
                } = chart;

                if (!isSet(ctx)) {
                  // leave if we do not have a valid context to draw to!
                  return;
                }
                // calculate the middle and radius of the circle we want to draw
                const centerX = (left + right) / 2;
                const centerY = (top + bottom) / 2;
                const r = Math.min(right - left, bottom - top) / 1.9;
                let infoText: string | undefined = undefined;

                // check if any dataset is available -> otherwise just draw a text with
                // "internal-error"
                if (datasets !== undefined) {
                  // loop thru all data-sets and check if any of the datasets has valid values
                  // and/or if the dataset is hidden (not selected)
                  for (let i = 0; i < datasets.length - 1; i += 1) {
                    const dataset = datasets[i];
                    for (let i_data = 0; i_data < dataset.data.length; i_data += 1) {
                      allHidden =
                        (allHidden && chart.legend?.legendItems?.[i_data]?.hidden) ?? true;

                      hasData = hasData || (dataset.data[i_data] as number) > 0;
                    }
                  }

                  // if we have no data, or all of the tariffs are hidden (unselected)
                  if (!hasData || allHidden) {
                    // show circle -> prepare error-message
                    infoText = this.context.formatMessage(messages.noDataAvailable);
                  }
                  // otherwise do not draw anything additional
                } else {
                  infoText = this.context.formatMessage(messages.internalError);
                }

                if (isSet(infoText)) {
                  ctx.beginPath();
                  ctx.lineWidth = width || 2;
                  ctx.strokeStyle = color || "rgba(0, 0, 0, 0.5)";
                  ctx.arc(centerX, centerY, r - radiusDecrease || 0, 0, 2 * Math.PI);
                  ctx.font = "30px serif";
                  ctx.fillStyle = color || "rgba(0, 0, 0, 0.5)";
                  ctx.textAlign = "center";
                  ctx.fillText(infoText, centerX, centerY);
                  ctx.stroke();
                }
              },
            },
          ]}
        />
      </div>
    );
  }

  // render the component
  public render() {
    // must be called in every derrived class from ChartBase!!!
    // the field allTariffCircuits is getting prepared within that function
    this.prepareHierarchyData();
    return <div id="piechart">{this.renderPieView()}</div>;
  }

  private legendItemClick(
    evt: ChartEvent,
    legendItem: LegendItem,
    legend: LegendElement<ChartType>,
  ) {
    const index = legendItem.index ?? 0;
    const wasHidden = !this.state.hiddenData.includes(index);
    const mouseEvent = evt.native as PointerEvent;
    const itemCnt = legend.chart.data.datasets[0].data.length;

    const hiddenData =
      mouseEvent?.shiftKey ?
        wasHidden === false || Math.abs(this.state.hiddenData.length - itemCnt) !== 1 ?
          Array.from(Array(itemCnt).keys()).filter((idx) => idx !== index)
        : []
      : [
          ...this.state.hiddenData.filter((di) => di !== index),
          ...(wasHidden === true ? [index] : []),
        ];

    this.setState({ hiddenData });

    return;
  }

  private onChartClick(event: ChartEvent, elements: ActiveElement[]) {
    // tariff clicked -> use as new root
    if (
      this.props.isHierarchyViewMode === false &&
      (event.native as PointerEvent).metaKey === false &&
      (event.native as PointerEvent).ctrlKey === false
    ) {
      return;
    }

    const clickedPieElement = elements.length ? elements[0] : undefined;

    if (isSet(clickedPieElement)) {
      // since we place the root-hierarch on top / at last to the datasets
      // so check the dataIndex / datasetIndex ->
      // use the first entry if dataset 1 is queried
      // use the dataIndex + 1 to jump over the root-entry
      const clickedTariff =
        clickedPieElement.datasetIndex === 0 ?
          this.tlEntries[clickedPieElement.index + 1]
        : this.tlEntries[0];
      const clickedTariffIdIdx = clickedTariff.id;

      const canClick =
        (clickedPieElement.datasetIndex === 0 && clickedTariff.hasChildren === true) ||
        (clickedPieElement.datasetIndex > 0 && isSet(clickedTariff.parentId));

      if (canClick && isSet(clickedTariffIdIdx)) {
        const tcModel = new TariffCircuitListModel(
          this.props.tariffCircuits,
          this.props.energyMeters,
        );

        const newSelectedTcIds = () => {
          if (this.props.isHierarchyViewMode === true) {
            return clickedPieElement.datasetIndex === 0 ?
                // select this child-tariff as new root
                this.prepareNextLevel(tcModel, clickedTariffIdIdx)
                // select the parent of this current root tariff as new root
              : tcModel.updateHierarchySelection(
                  this.allTariffCircuits,
                  [clickedTariffIdIdx],
                  undefined,
                );
          }

          const allCurrentIds =
            isSet(this.props.selection?.tariffCircuitIds) ?
              this.props.selection?.tariffCircuitIds
            : this.props.tariffCircuits.map((item, idx) => item.id ?? idx);

          const childIds =
            isSet(allCurrentIds.find((tcId) => tcId === clickedTariffIdIdx)) ?
              (allCurrentIds.filter((tcId) => clickedTariffIdIdx !== tcId) as number[])
            : (allCurrentIds.concat([clickedTariffIdIdx]) as number[]);

          return childIds;
        };

        this.props.onHierarchyChanged(newSelectedTcIds(), this.props.isHierarchyViewMode);
      }
    }
  }

  private checkMouse(elements: ActiveElement[]) {
    // tariff hovered -> show different mouse-pointer
    const hoveredPieElement = elements.length ? elements[0] : undefined;
    const element = document.getElementById("piechart")?.getElementsByTagName("canvas")[0];

    if (isSet(hoveredPieElement)) {
      if (isSet(element)) {
        // since we place the root-hierarch on top / at last to the datasets
        // so check the dataIndex / datasetIndex ->
        // use the first entry if dataset 1 is queried
        // use the dataIndex + 1 to jump over the root-entry
        const hoveredTariff =
          hoveredPieElement.datasetIndex === 0 ?
            this.tlEntries[hoveredPieElement.index + 1]
          : this.tlEntries[0];

        const isClickable =
          this.props.isHierarchyViewMode === false ||
          (hoveredPieElement.datasetIndex === 0 && hoveredTariff.hasChildren === true) ||
          (hoveredPieElement.datasetIndex > 0 && isSet(hoveredTariff.parentId));

        element.style.cursor = isClickable ? "pointer" : "default";
      }
    } else if (isSet(element)) {
      element.style.cursor = "default";
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PowerPieChartComponent);
